import { faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import styled from 'styled-components';

import { MAX_RATING, MIN_RATING, RATING_LABELS } from './rating';

const Stars = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const StarButton = styled.button<{ filled: boolean; size: number }>`
  border: none;
  background: none;
  padding: 2px;
  font-size: ${({ size }) => size}px;
  line-height: 1;
  cursor: pointer;
  color: ${({ filled }) =>
    filled ? 'rgb(230, 160, 20)' : 'rgb(210, 210, 210)'};
  transition: transform 0.1s ease;

  &:hover {
    transform: scale(1.1);
  }

  &:disabled {
    cursor: default;
    transform: none;
  }
`;

const Label = styled.span`
  margin-left: 8px;
  font-size: 1.05em;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

interface Props {
  value?: number;
  onChange: (rating: number) => void;
  disabled?: boolean;
  /** Stjärnornas storlek i px. */
  size?: number;
  /** Visa ordet ("Bra") bredvid stjärnorna. */
  showLabel?: boolean;
}

/** Betyget 1–5 som klickbara stjärnor, se rating.ts. */
const StarRating: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  size = 26,
  showLabel = true,
}) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const shown = hovered ?? value ?? 0;

  return (
    <Stars role="radiogroup" onMouseLeave={() => setHovered(null)}>
      {Array.from(
        { length: MAX_RATING - MIN_RATING + 1 },
        (_, i) => MIN_RATING + i
      ).map((rating) => (
        <StarButton
          aria-label={`${rating} av ${MAX_RATING} – ${RATING_LABELS[rating]}`}
          aria-checked={value === rating}
          role="radio"
          disabled={disabled}
          filled={rating <= shown}
          key={rating}
          onClick={() => onChange(rating)}
          onMouseEnter={() => setHovered(rating)}
          size={size}
          title={RATING_LABELS[rating]}
          type="button"
        >
          <FontAwesomeIcon icon={faStar} />
        </StarButton>
      ))}
      {showLabel && (
        <Label>{RATING_LABELS[shown] || 'Välj 1–5 stjärnor'}</Label>
      )}
    </Stars>
  );
};

export default StarRating;
